import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Cpu, Key, CheckCircle2, XCircle, Loader2, Play, 
  Settings, BarChart3, Zap, ExternalLink, Eye, EyeOff,
  Sparkles, Brain, MessageSquare, Save, Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminOpenRouter() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [config, setConfig] = useState(null);
  const [models, setModels] = useState([]);
  const [usage, setUsage] = useState(null);
  
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [defaultModel, setDefaultModel] = useState('meta-llama/llama-3.2-3b-instruct:free');
  const [enabledModels, setEnabledModels] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [configRes, modelsRes, usageRes] = await Promise.all([
        fetch(`${API_URL}/api/openrouter/config`, { headers }),
        fetch(`${API_URL}/api/openrouter/models`, { headers }),
        fetch(`${API_URL}/api/openrouter/usage?days=7`, { headers })
      ]);

      if (configRes.ok) {
        const configData = await configRes.json();
        setConfig(configData);
        setDefaultModel(configData.default_model || 'meta-llama/llama-3.2-3b-instruct:free');
        setEnabledModels(configData.enabled_models || []);
      }
      
      if (modelsRes.ok) {
        const modelsData = await modelsRes.json();
        setModels(modelsData.models || []);
        if (enabledModels.length === 0) {
          setEnabledModels(modelsData.models?.map(m => m.id) || []);
        }
      }
      
      if (usageRes.ok) {
        setUsage(await usageRes.json());
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!apiKey && !config?.api_key_set) {
      toast.error('Ingresa tu API Key de OpenRouter');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/openrouter/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          api_key: apiKey || config?.api_key,
          default_model: defaultModel,
          enabled_models: enabledModels
        })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success('Configuración guardada correctamente');
        setApiKey('');
        loadData();
      } else {
        toast.error(data.detail || 'Error al guardar configuración');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async () => {
    if (!window.confirm('¿Estás seguro de eliminar la configuración de OpenRouter?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/openrouter/config`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Configuración eliminada');
      setConfig(null);
      setApiKey('');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const testModel = async (modelId) => {
    setTesting(modelId);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/openrouter/models/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          model_id: modelId,
          test_prompt: 'Responde con un saludo corto y amigable en español.'
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(
          <div>
            <p className="font-medium">Modelo funcionando</p>
            <p className="text-sm text-muted-foreground">{data.response}</p>
          </div>
        );
      } else {
        toast.error(data.error || 'Error al probar modelo');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setTesting(null);
    }
  };

  const toggleModel = (modelId) => {
    if (enabledModels.includes(modelId)) {
      setEnabledModels(enabledModels.filter(id => id !== modelId));
    } else {
      setEnabledModels([...enabledModels, modelId]);
    }
  };

  const toggleAllModels = () => {
    if (enabledModels.length === models.length) {
      setEnabledModels([]);
    } else {
      setEnabledModels(models.map(m => m.id));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="openrouter-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Brain className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">OpenRouter</h1>
            <p className="text-muted-foreground">Acceso a múltiples modelos de IA gratuitos</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={config?.configured ? 'default' : 'secondary'} className="gap-1">
            {config?.configured ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
            {config?.configured ? 'Configurado' : 'No configurado'}
          </Badge>
          <Button variant="outline" size="sm" asChild>
            <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" />
              Obtener API Key
            </a>
          </Button>
        </div>
      </div>

      <Tabs defaultValue="config">
        <TabsList>
          <TabsTrigger value="config" className="gap-2">
            <Settings className="h-4 w-4" />
            Configuración
          </TabsTrigger>
          <TabsTrigger value="models" className="gap-2">
            <Cpu className="h-4 w-4" />
            Modelos
          </TabsTrigger>
          <TabsTrigger value="usage" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Uso
          </TabsTrigger>
        </TabsList>

        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Key className="h-5 w-5" />
                API Key de OpenRouter
              </CardTitle>
              <CardDescription>
                Obtén tu API key gratuita en <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">openrouter.ai/keys</a>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>API Key</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showApiKey ? 'text' : 'password'}
                      placeholder={config?.api_key_set ? 'API key configurada (ingresa nueva para cambiar)' : 'sk-or-v1-xxxxxxxxxxxxx'}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowApiKey(!showApiKey)}
                    >
                      {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                {config?.api_key_set && (
                  <p className="text-sm text-muted-foreground">
                    API Key actual: {config.api_key_masked}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Modelo por Defecto</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border bg-background"
                  value={defaultModel}
                  onChange={(e) => setDefaultModel(e.target.value)}
                >
                  {models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name} ({model.provider})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={saveConfig} disabled={saving} className="gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Guardar Configuración
                </Button>
                {config?.configured && (
                  <Button variant="outline" onClick={deleteConfig} className="gap-2 text-destructive">
                    <Trash2 className="h-4 w-4" />
                    Eliminar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quick Info */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Sparkles className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="font-medium">100% Gratuito</p>
                    <p className="text-sm text-muted-foreground">Sin costos ocultos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Cpu className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="font-medium">{models.length} Modelos</p>
                    <p className="text-sm text-muted-foreground">Disponibles</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <MessageSquare className="h-8 w-8 text-purple-500" />
                  <div>
                    <p className="font-medium">{usage?.total_requests || 0}</p>
                    <p className="text-sm text-muted-foreground">Requests (7 días)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Models Tab */}
        <TabsContent value="models" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Modelos de IA Gratuitos</CardTitle>
                  <CardDescription>Selecciona los modelos que deseas habilitar para los agentes IA</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={toggleAllModels}>
                  {enabledModels.length === models.length ? 'Deshabilitar todos' : 'Habilitar todos'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {models.map((model) => (
                  <Card key={model.id} className={`transition-all ${enabledModels.includes(model.id) ? 'border-primary/50 bg-primary/5' : 'opacity-60'}`}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                            model.provider === 'Meta' ? 'bg-blue-500/20' :
                            model.provider === 'Google' ? 'bg-red-500/20' :
                            model.provider === 'Microsoft' ? 'bg-cyan-500/20' :
                            model.provider === 'Mistral AI' ? 'bg-orange-500/20' :
                            'bg-purple-500/20'
                          }`}>
                            <Brain className={`h-5 w-5 ${
                              model.provider === 'Meta' ? 'text-blue-500' :
                              model.provider === 'Google' ? 'text-red-500' :
                              model.provider === 'Microsoft' ? 'text-cyan-500' :
                              model.provider === 'Mistral AI' ? 'text-orange-500' :
                              'text-purple-500'
                            }`} />
                          </div>
                          <div>
                            <h4 className="font-medium">{model.name}</h4>
                            <p className="text-sm text-muted-foreground">{model.provider}</p>
                            <p className="text-xs text-muted-foreground mt-1">{model.description}</p>
                            <div className="flex gap-2 mt-2">
                              <Badge variant="outline" className="text-xs">
                                {(model.context_length / 1000).toFixed(0)}K tokens
                              </Badge>
                              {defaultModel === model.id && (
                                <Badge className="text-xs">Por defecto</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Switch
                            checked={enabledModels.includes(model.id)}
                            onCheckedChange={() => toggleModel(model.id)}
                          />
                          {config?.configured && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => testModel(model.id)}
                              disabled={testing === model.id}
                            >
                              {testing === model.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Play className="h-3 w-3" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {enabledModels.length !== (config?.enabled_models?.length || 0) && (
                <div className="mt-4 flex justify-end">
                  <Button onClick={saveConfig} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Guardar cambios
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Usage Tab */}
        <TabsContent value="usage" className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{usage?.total_requests || 0}</p>
                    <p className="text-sm text-muted-foreground">Requests totales</p>
                  </div>
                  <MessageSquare className="h-8 w-8 text-muted-foreground/30" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{usage?.total_tokens?.toLocaleString() || 0}</p>
                    <p className="text-sm text-muted-foreground">Tokens utilizados</p>
                  </div>
                  <Zap className="h-8 w-8 text-muted-foreground/30" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{usage?.period_days || 7} días</p>
                    <p className="text-sm text-muted-foreground">Período</p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-muted-foreground/30" />
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Uso por Modelo</CardTitle>
            </CardHeader>
            <CardContent>
              {usage?.by_model?.length > 0 ? (
                <div className="space-y-3">
                  {usage.by_model.map((stat, idx) => (
                    <div key={idx} className="flex items-center gap-4">
                      <div className="flex-1">
                        <p className="text-sm font-medium truncate">{stat._id}</p>
                        <div className="flex gap-4 text-xs text-muted-foreground">
                          <span>{stat.total_requests} requests</span>
                          <span>{stat.total_tokens?.toLocaleString()} tokens</span>
                        </div>
                      </div>
                      <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${(stat.total_requests / (usage?.total_requests || 1)) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No hay datos de uso aún. Comienza a usar los modelos para ver estadísticas.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
